import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";

import type { LedgerTransaction, Reconciliation } from "@ravnar/api-contract";

import { ApiError } from "../../api/client";
import { ledgerPageQueryOptions, reconciliationQueryOptions, updateLedger } from "../../api/reconciliation";
import { useSession } from "../../api/session";
import { PageHeader } from "../../components/shell/app-shell";
import { Button } from "../../components/ui/button";
import { buttonClassName } from "../../components/ui/button-styles";
import { Card, CardHeader } from "../../components/ui/card";
import { Empty } from "../../components/ui/empty";
import { FilterPill, Toolbar } from "../../components/ui/filter-pill";
import { Pager } from "../../components/ui/pager";
import { Pill } from "../../components/ui/pill";
import { Skeleton } from "../../components/ui/skeleton";
import { Table, type TableColumn } from "../../components/ui/table";
import {
	LEDGER_STATUS_LABELS,
	LEDGER_STATUS_TONES,
	LEDGER_STATUSES,
	LEDGER_SYNC_PROBLEMS,
	type LedgerStatus,
} from "../../domain/reconciliation";
import { cn } from "../../lib/cn";
import { formatAmount, formatMonthDay, formatRelativeTime, formatShortDate } from "../../lib/format";
import { showToast } from "../../lib/toast";
import { TransactionDrawer } from "./-components/transaction-drawer";

// `.catch()` turns a hand-edited bad value into the fallback.
const reconciliationSearchSchema = z.object({
	/** The status the ledger is narrowed to; every status when there is none. */
	status: z.enum(LEDGER_STATUSES).optional().catch(undefined),
	/** A page link from the API, which the page never reads; the newest page when there is none. */
	page: z.string().optional().catch(undefined),
	/** The transaction the drawer shows; one this year's ledger does not hold says so there. */
	transaction: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/finance/reconciliation")({
	validateSearch: reconciliationSearchSchema,
	head: () => ({ meta: [{ title: "Reconciliation · Finance · Ravnar" }] }),
	staticData: { crumbs: ["Finance", "Reconciliation"] },
	component: ReconciliationPage,
});

function ReconciliationPage() {
	const { member } = useSession();
	const canRead = member.access.finance !== "none";
	const queryClient = useQueryClient();
	// A read that got the ledger says so in the toast; one that could not is said where the ledger stands, under the
	// header, and one not done in time says so there too. Either way every page of the ledger is asked for again.
	const update = useMutation({
		mutationFn: () => updateLedger(queryClient),
		onSuccess: figures => {
			if (figures?.sync.lastAttempt?.outcome === "synced") {
				showToast("Ledger updated.");
			}
			void queryClient.invalidateQueries({ queryKey: ["reconciliation"] });
		},
	});
	return (
		<>
			<PageHeader
				title="Reconciliation"
				description="Every bank transaction of the year, matched to the document behind it, or marked as needing none."
				actions={
					<>
						<Link to="/finance" className={buttonClassName({ size: "md" })}>
							Back to Finance
						</Link>
						{canRead ? (
							<Button
								variant="primary"
								disabled={update.isPending}
								onClick={() => {
									update.mutate();
								}}
							>
								{update.isPending ? "Updating…" : "Update ledger"}
							</Button>
						) : null}
					</>
				}
			/>
			{update.isError ? (
				<p role="alert" className="mb-3.5 text-caption text-alert">
					{update.error.message}
				</p>
			) : null}
			{update.isSuccess && update.data === undefined ? (
				<p role="status" className="mb-3.5 text-caption text-ink-3">
					The bank has not been read yet. It will be: look at the ledger again in a few minutes.
				</p>
			) : null}
			{canRead ? (
				<Ledger />
			) : (
				<Card>
					<Empty>Only members with access to Finance see the ledger.</Empty>
				</Card>
			)}
		</>
	);
}

/**
 * Where the ledger stands, in a line or two under the header: when it was last read from the bank, why the last read
 * failed, and from which day it holds every transaction when that is after January 1 — the part of the year Revolut
 * would no longer hand over when it was read.
 */
function LedgerStanding({ reconciliation }: { readonly reconciliation: Reconciliation }) {
	const { member } = useSession();
	const { year, sync } = reconciliation;
	const attempt = sync.lastAttempt;
	const problem =
		attempt === undefined || attempt.outcome === "synced"
			? undefined
			: { at: attempt.at, outcome: attempt.outcome };
	const partial = sync.coveredFrom !== undefined && sync.coveredFrom > `${String(year)}-01-01`;
	return (
		<div className="mb-3.5 flex flex-col gap-1 text-caption text-ink-3">
			<p>
				{sync.syncedAt === undefined
					? `Not read from the bank in ${String(year)} yet.`
					: `Read from Revolut ${formatRelativeTime(sync.syncedAt)}.`}
			</p>
			{problem === undefined ? null : (
				<p className="text-warn">
					{`${LEDGER_SYNC_PROBLEMS[problem.outcome]} (${formatRelativeTime(problem.at)}).`}
					{problem.outcome !== "failed" && member.role !== "member" ? (
						<>
							{" "}
							<Link to="/settings/revolut" className="text-accent hover:underline">
								Revolut settings
							</Link>
						</>
					) : null}
				</p>
			)}
			{partial && sync.coveredFrom !== undefined ? (
				<p>
					{`It holds every transaction from ${formatShortDate(sync.coveredFrom)}: Revolut hands the rest of ${String(year)} over only when it is connected again.`}
				</p>
			) : null}
		</div>
	);
}

/**
 * What a transaction was, and with whom: the bank's description, which opens the transaction in the drawer, then who
 * it names and the reference, if any.
 */
function TransactionCell({ transaction }: { readonly transaction: LedgerTransaction }) {
	const { id, description, counterparty, reference } = transaction;
	const also = [counterparty === description ? undefined : counterparty, reference].filter(
		(part): part is string => part !== undefined,
	);
	return (
		<span className="block min-w-0">
			<Link
				from="/finance/reconciliation"
				to="/finance/reconciliation"
				search={previous => ({ ...previous, transaction: id })}
				className="block font-medium text-ink hover:underline"
			>
				{description}
			</Link>
			{also.length === 0 ? null : <span className="block text-caption text-ink-3">{also.join(" · ")}</span>}
		</span>
	);
}

const ledgerColumns: readonly TableColumn<LedgerTransaction>[] = [
	{
		key: "booked",
		header: "Booked",
		cell: transaction => (
			<time dateTime={transaction.bookedOn} className="whitespace-nowrap">
				{formatMonthDay(transaction.bookedOn)}
			</time>
		),
	},
	{ key: "transaction", header: "Transaction", cell: transaction => <TransactionCell transaction={transaction} /> },
	{ key: "account", header: "Account", muted: true, cell: transaction => transaction.account.name },
	{
		key: "amount",
		header: "Amount",
		align: "right",
		cell: ({ amount }) => (
			<span className={cn("whitespace-nowrap", !amount.amount.startsWith("-") && "text-ok")}>
				{formatAmount(amount.amount, amount.currency)}
			</span>
		),
	},
	{
		key: "status",
		header: "Status",
		cell: transaction => (
			<Pill tone={LEDGER_STATUS_TONES[transaction.status]}>{LEDGER_STATUS_LABELS[transaction.status]}</Pill>
		),
	},
];

/** What the card says when the list it shows is empty: a status with none, a year with none, or a page emptied. */
const EMPTY_STATUS_TEXT: Readonly<Record<LedgerStatus, string>> = {
	"needs-document": "No transaction needs a document.",
	"to-confirm": "Nothing is waiting to be confirmed.",
	matched: "No transaction is matched to a document yet.",
	"no-document": "No transaction is marked as needing no document.",
};

function plural(count: number, singular: string, pluralForm: string): string {
	return `${String(count)} ${count === 1 ? singular : pluralForm}`;
}

/**
 * The year's ledger, a page at a time, the newest transaction first, narrowed to one status by the pills, which count
 * every status of the year whichever is shown.
 */
function Ledger() {
	const { status, page, transaction: opened } = Route.useSearch();
	const navigate = Route.useNavigate();
	const figures = useQuery(reconciliationQueryOptions());
	const shown = useQuery(ledgerPageQueryOptions({ status, page }));
	// A page link the API no longer takes, from an old bookmark or edited by hand: the newest page of the same status.
	const refused = page !== undefined && shown.error instanceof ApiError && shown.error.status === 400;
	useEffect(() => {
		if (refused) {
			void navigate({ search: { status }, replace: true });
		}
	}, [refused, status, navigate]);

	/** Shows one status, or every status. A page link names the list it was made for, so the page goes. */
	function showStatus(option: LedgerStatus | undefined) {
		void navigate({ search: { status: option }, replace: true });
	}

	const counts = shown.data?.counts;
	const everyStatus = counts === undefined ? undefined : LEDGER_STATUSES.reduce((sum, each) => sum + counts[each], 0);
	return (
		<>
			{figures.data === undefined ? null : <LedgerStanding reconciliation={figures.data} />}
			<Toolbar>
				<FilterPill
					active={status === undefined}
					count={everyStatus}
					onClick={() => {
						showStatus(undefined);
					}}
				>
					All
				</FilterPill>
				{LEDGER_STATUSES.map(option => (
					<FilterPill
						key={option}
						active={status === option}
						count={counts?.[option]}
						onClick={() => {
							showStatus(status === option ? undefined : option);
						}}
					>
						{LEDGER_STATUS_LABELS[option]}
					</FilterPill>
				))}
			</Toolbar>
			<Card>
				<CardHeader
					title={
						shown.data === undefined
							? "Ledger"
							: `${plural(shown.data.total, "transaction", "transactions")} in ${String(shown.data.year)}`
					}
				/>
				{refused || shown.isPending ? (
					<div role="status" aria-label="Loading the ledger" className="flex flex-col gap-3 p-4">
						<Skeleton className="h-3.5 w-2/3" />
						<Skeleton className="h-3.5 w-1/2" />
						<Skeleton className="h-3.5 w-3/5" />
					</div>
				) : shown.isError ? (
					<p role="alert" className="px-4 py-3.5 text-caption text-alert">
						{shown.error.message}
					</p>
				) : shown.data.transactions.length > 0 ? (
					<Table
						label="Ledger"
						columns={ledgerColumns}
						rows={shown.data.transactions}
						rowKey={transaction => transaction.id}
						interactiveRows
					/>
				) : (
					<Empty>
						{shown.data.total > 0
							? "Nothing is left on this page."
							: status === undefined
								? `No bank transactions in ${String(shown.data.year)} yet.`
								: EMPTY_STATUS_TEXT[status]}
					</Empty>
				)}
				{shown.data === undefined || (shown.data.next === null && shown.data.previous === null) ? null : (
					<Pager
						label="Ledger pages"
						previous={
							shown.data.previous === null ? null : (
								<Link
									to="/finance/reconciliation"
									search={{ status, page: shown.data.previous }}
									className={buttonClassName({ size: "sm" })}
								>
									Previous
								</Link>
							)
						}
						next={
							shown.data.next === null ? null : (
								<Link
									to="/finance/reconciliation"
									search={{ status, page: shown.data.next }}
									className={buttonClassName({ size: "sm" })}
								>
									Next
								</Link>
							)
						}
					/>
				)}
			</Card>
			<TransactionDrawer
				transactionId={opened}
				onClose={() => {
					void navigate({ search: previous => ({ ...previous, transaction: undefined }), replace: true });
				}}
			/>
		</>
	);
}
